
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { Link } from 'react-router-dom';

export default function Home() {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/a600beb6f3db793faa9a8ce3e316aa9e.jpg')`
        }}
      >
        <div className="absolute inset-0 bg-emerald-900/70"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Breaking the Cycle,<br />
              <span className="text-yellow-300">Building Hope</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed">
              For over 16 years, IRN has been closing the revolving door between the justice system and homelessness through comprehensive, individualized support services.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="primary" size="lg">
                <i className="ri-heart-line mr-2"></i>
                Get Support
              </Button>
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-emerald-900">
                <i className="ri-information-line mr-2"></i>
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Mission</h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              To close the revolving door between the justice system and homelessness by providing housing, case management, employment and mental health therapy.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-home-heart-line text-2xl text-emerald-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Housing First</h3>
              <p className="text-gray-600 leading-relaxed">
                We believe stable housing is the foundation for recovery and success. Our Housing First approach provides immediate access to permanent housing without preconditions.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-user-heart-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Lived Experience</h3>
              <p className="text-gray-600 leading-relaxed">
                All IRN staff have lived experience of justice involvement and/or housing insecurity, bringing authentic understanding and peer support to our services.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-team-line text-2xl text-emerald-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">High-Touch Support</h3>
              <p className="text-gray-600 leading-relaxed">
                Our individualized, strength-based approach provides comprehensive case management, employment support, and mental health services tailored to each person's needs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive support services informed by evidence-based practices including Housing First, Harm Reduction, Motivational Interviewing, and Trauma-Informed care.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center p-6 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors">
              <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-building-2-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Housing Support</h3>
              <p className="text-gray-600 text-sm">Permanent supportive housing and rental assistance</p>
            </div>
            
            <div className="text-center p-6 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-briefcase-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Employment Services</h3>
              <p className="text-gray-600 text-sm">Job training, placement, and career development</p>
            </div>
            
            <div className="text-center p-6 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors">
              <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-heart-pulse-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Mental Health</h3>
              <p className="text-gray-600 text-sm">Therapy and counseling services</p>
            </div>
            
            <div className="text-center p-6 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-compass-3-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Case Management</h3>
              <p className="text-gray-600 text-sm">System navigation and individualized support</p>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <Link to="/services">
              <Button variant="primary" size="lg">
                Explore All Services
                <i className="ri-arrow-right-line ml-2"></i>
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Impact Statistics */}
      <section 
        className="py-20 bg-cover bg-center relative"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=Professional%20office%20environment%20with%20diverse%20team%20members%20collaborating%2C%20warm%20natural%20lighting%2C%20modern%20workspace%20with%20charts%20and%20success%20metrics%20visible%2C%20people%20of%20different%20backgrounds%20working%20together%2C%20inspiring%20corporate%20photography%2C%20clean%20and%20organized%20setting%2C%20hope%20and%20progress%20theme&width=1920&height=800&seq=impact-bg&orientation=landscape')`
        }}
      >
        <div className="absolute inset-0 bg-emerald-900/80"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-6">Our Impact Since 2008</h2>
            <p className="text-xl text-emerald-100 max-w-3xl mx-auto">
              18+ years of transforming lives through evidence-based, individualized support
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-5xl font-bold text-yellow-300 mb-2">2,500+</div>
              <p className="text-emerald-100">Individuals Served</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-yellow-300 mb-2">85%</div>
              <p className="text-emerald-100">Housing Retention Rate</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-yellow-300 mb-2">18+</div>
              <p className="text-emerald-100">Years of Service</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-yellow-300 mb-2">70%</div>
              <p className="text-emerald-100">Employment Success</p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Core Values</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Diversity, Equity, and Inclusion are embedded in our operations, staffing, and culture
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-diversity-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Diversity & Inclusion</h3>
              <p className="text-gray-600 leading-relaxed">
                We celebrate and embrace the diverse backgrounds, experiences, and perspectives of all community members we serve and work alongside.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Trauma-Informed</h3>
              <p className="text-gray-600 leading-relaxed">
                Our approach recognizes the widespread impact of trauma and integrates this understanding into all aspects of our service delivery.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-hand-heart-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Harm Reduction</h3>
              <p className="text-gray-600 leading-relaxed">
                We meet people where they are, focusing on reducing harm and supporting individual choices in their recovery journey.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-emerald-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Make a Difference?</h2>
          <p className="text-xl text-emerald-100 mb-8 max-w-3xl mx-auto">
            Whether you need support or want to support others, we're here to help. Join us in breaking the cycle and building hope in our community.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="secondary" size="lg">
              <i className="ri-phone-line mr-2"></i>
              Get Help Now
            </Button>
            <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-emerald-600">
              <i className="ri-hand-heart-line mr-2"></i>
              Support Our Mission
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
